/**
 * 回答正文里知识库文件链接的解析（纯函数，不依赖 UI / store）。
 *
 * 后端约定：提到知识库文件一律输出标准 Markdown 链接
 *   [描述](/preview/<file_id>?page=12 "文件名.pdf")
 * 只认 /preview/ 前缀，不靠 UUID 嗅探（客户文档里的 S3 / SharePoint 外链也带 UUID）。
 */

export const FILE_PREVIEW_LINK_PREFIX = '/preview/';

export type FileChipKind = 'pdf' | 'word' | 'ppt' | 'excel' | 'image' | 'web' | 'file';

const SUFFIX_KIND: Array<[RegExp, FileChipKind]> = [
  [/\.pdf$/i, 'pdf'],
  [/\.docx?$/i, 'word'],
  [/\.pptx?$/i, 'ppt'],
  [/\.(xlsx?|xlsm|csv)$/i, 'excel'],
  [/\.(png|jpe?g|gif|webp|svg)$/i, 'image'],
  [/\.html?$/i, 'web'],
];

/** 按文件名（或 "x.<type>" 形式）后缀判定类型，判不出返回 undefined */
export const resolveFileChipKind = (name?: string | null): FileChipKind | undefined => {
  const trimmed = name?.trim();
  if (!trimmed) return undefined;
  return SUFFIX_KIND.find(([regex]) => regex.test(trimmed))?.[1];
};

export interface FilePreviewLink {
  fileId: string;
  /**
   * 预览器的页索引（从 0 起）。
   * 链接里的 ?page= 是给人看的页码（从 1 起，与回答里的 "p.5" 一致），这里已减 1；缺省或非法为 0。
   */
  page: number;
  sheet: string;
  rows: number[];
  type?: string;
}

/**
 * 解析 /preview/<file_id>?page=12&sheet=1&rows=2&rows=3 这类链接。
 * 不是 /preview/ 前缀或没有 file_id 时返回 null，调用方按普通链接处理。
 *
 * 兼容两种写法：
 * - 模型偶尔会把 prompt 里的路由占位符 ":" 也抄进来（/preview/:<uuid>），这里剥掉
 * - rows 既可能是重复 query（rows=2&rows=3）也可能是逗号拼接（rows=2,3）
 */
export const parseFilePreviewLink = (href?: string): FilePreviewLink | null => {
  if (!href?.startsWith(FILE_PREVIEW_LINK_PREFIX)) return null;

  const [pathPart, queryPart = ''] = href.split('?');
  const fileId = pathPart.slice(FILE_PREVIEW_LINK_PREFIX.length).replace(/^:/, '').split('/')[0].trim();
  if (!fileId) return null;

  const params = new URLSearchParams(queryPart);
  const rows = params
    .getAll('rows')
    .flatMap((value) => value.split(','))
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isInteger(value) && value >= 0);

  return {
    fileId,
    page: Math.max(0, (Number(params.get('page')) || 0) - 1),
    sheet: params.get('sheet') || '',
    rows,
    type: params.get('type') || undefined,
  };
};
